import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import 'chart.js/auto';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FaSyncAlt, FaFileExcel, FaFileCsv, FaFilePdf, FaDownload, FaChevronDown, FaPaperPlane, FaComments } from 'react-icons/fa';
import {
  getReportDetail, getQaReportStatus, regenerateReport,
  startExecutiveSummary, getExecutiveSummaryStatus, postReportChat,
} from '../api/api';
import { SpreadsheetGrid } from '../components/SpreadsheetGrid';
import { Select } from '../components/Select';
import LoadingState, { InlineSpinner } from '../components/LoadingState';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 90; // ~3 minutes

const RANK_LABELS = {
  qa_top: 'Top 20 by QA Score',
  qa_bottom: 'Bottom 20 by QA Score',
  compliance_top: 'Top 20 by Compliance Score',
  compliance_bottom: 'Bottom 20 by Compliance Score',
  assessed: 'Assessed calls only',
  unassessed: 'Unassessed calls only',
};

// Must match SALES_FILTER_LABELS in pca-exec-summary's lib/common.js.
const SALES_LABELS = {
  sales: 'Sales calls only',
  non_sales: 'Non-sales calls only',
};

// Reports created before the Dates by choice have no dateBasis and were matched by call date.
const DATE_BASIS_LABELS = {
  call: 'Call date',
  processed: 'Processed date',
};

const RANK_OPTIONS = [
  { value: 'qa_top', label: 'Top 20 by QA Score' },
  { value: 'qa_bottom', label: 'Bottom 20 by QA Score' },
  { value: 'compliance_top', label: 'Top 20 by Compliance Score' },
  { value: 'compliance_bottom', label: 'Bottom 20 by Compliance Score' },
  { value: 'assessed', label: 'Assessed calls only' },
  { value: 'unassessed', label: 'Unassessed calls only' },
];

// Shows one report from the Reports page's persistent list: its stored filters (not
// any current global filter state — there isn't one on this page), the preview grid,
// and the XLSX/CSV exports and PDF summary for it. XLSX and CSV are kicked off
// server-side the moment the grid itself finishes (see report-grid-worker.js) — so by
// the time anyone opens this page they're normally already done, and this page just
// reads their status off the report record (DynamoDB) rather than starting anything
// new or touching S3 at all until Download is actually clicked. A failed XLSX/CSV run
// offers Retry (mode=xlsx/csv&retry=true), which re-invokes that one worker against
// the same query rather than paying for a fresh Athena run. None of the three gates
// the other two — that's the whole reason they're separate Lambdas (see
// cfn/lib/exec-summary-api.template).
const ReportDetail = ({ setAlert }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Regenerate — re-runs this report's own query (e.g. after a client manually
  // overrides a call) and rebuilds preview/XLSX/CSV under a new queryExecutionId; PDF
  // tracking is cleared too (see the backend's regenerateReport) but not auto-started,
  // same as any other report — Generate PDF stays an explicit, on-demand click.
  const [regenerating, setRegenerating] = useState(false);

  // Preview grid — reflects the report's own queryExecutionId, whether that's still
  // processing (poll until done) or already complete (fetch once). previewRankBy
  // starts out matching the report's own stored Focus filter but can be changed here
  // without touching the report record — a different S3 key (see report-pivot.js's
  // rank suffix), re-pivoted from the same cached Athena results, no new query.
  const [gridStatus, setGridStatus] = useState('idle'); // idle | running | succeeded | failed
  const [grid, setGrid] = useState(null);
  const [gridError, setGridError] = useState(null);
  const [previewRankBy, setPreviewRankBy] = useState('');

  // XLSX export — same queryExecutionId as the grid, own worker (see
  // cfn/lib/exec-summary-api.template for why it's split out).
  const [xlsxStatus, setXlsxStatus] = useState('idle'); // idle | running | succeeded | failed
  const [xlsxError, setXlsxError] = useState(null);

  // CSV export — same full-dataset scope as XLSX, own worker (no exceljs).
  const [csvStatus, setCsvStatus] = useState('idle'); // idle | running | succeeded | failed
  const [csvError, setCsvError] = useState(null);

  // PDF export — its own job, own polling; the backend reuses the report's own
  // queryExecutionId when it can (skipping a second Athena scan) and transparently
  // falls back to running its own query when it can't, so nothing about that changes
  // from this page's perspective.
  const [execSummaryStatus, setExecSummaryStatus] = useState('idle'); // idle | starting | running | succeeded | failed
  const [execSummaryError, setExecSummaryError] = useState(null);
  const [execSummaryJobId, setExecSummaryJobId] = useState(null);
  const [questionAnalysisUrl, setQuestionAnalysisUrl] = useState(null);
  const [questionAnalysisError, setQuestionAnalysisError] = useState(null);

  // Download menu renders in a portal with fixed positioning (same pattern as
  // RefreshButton / the old Reports export menu) so ancestor overflow/stacking
  // contexts can never clip it.
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [downloadMenuPos, setDownloadMenuPos] = useState({ top: 0, left: 0 });
  const downloadBtnRef = useRef(null);
  const downloadMenuRef = useRef(null);
  const DOWNLOAD_MENU_WIDTH = 320; // w-80

  const updateDownloadMenuPos = useCallback(() => {
    if (!downloadBtnRef.current) return;
    const rect = downloadBtnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - DOWNLOAD_MENU_WIDTH - 8));
    setDownloadMenuPos({ top: rect.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        downloadBtnRef.current && !downloadBtnRef.current.contains(event.target) &&
        downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)
      ) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updateDownloadMenuPos, true);
    window.addEventListener('resize', updateDownloadMenuPos);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updateDownloadMenuPos, true);
      window.removeEventListener('resize', updateDownloadMenuPos);
    };
  }, [downloadMenuOpen, updateDownloadMenuPos]);

  const gridPollRef = useRef(null);
  const gridPollCountRef = useRef(0);
  const xlsxPollRef = useRef(null);
  const xlsxPollCountRef = useRef(0);
  const csvPollRef = useRef(null);
  const csvPollCountRef = useRef(0);
  const execSummaryPollRef = useRef(null);
  const execSummaryPollCountRef = useRef(0);

  const pollGrid = useCallback((queryExecutionId, filterSummary, rankBy) => {
    gridPollRef.current = setTimeout(async () => {
      gridPollCountRef.current += 1;
      try {
        const res = await getQaReportStatus(queryExecutionId, filterSummary, rankBy);
        if (res.status === 'RUNNING') {
          if (gridPollCountRef.current >= MAX_POLLS) {
            setGridStatus('failed');
            setGridError('Report generation is taking longer than expected. Please try again.');
            return;
          }
          pollGrid(queryExecutionId, filterSummary, rankBy);
          return;
        }
        if (res.status === 'FAILED') {
          setGridStatus('failed');
          setGridError(res.message || 'Report generation failed.');
          return;
        }
        setGrid(res);
        setGridStatus('succeeded');
      } catch (err) {
        console.error('Report status check failed:', err);
        setGridStatus('failed');
        setGridError('Failed to check report status. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, []);

  // Switches which Focus filter the preview shows, independent of the report's own
  // stored rankBy — the downloads (XLSX, CSV and both PDFs) always stay tied to the
  // report's original filter, only this grid view changes. Same query, same cached
  // Athena results underneath; a filter that's never been previewed for this report
  // just costs one cheap re-pivot, not a new Athena run (see report-grid-worker.js /
  // handleReportStatus's mode=grid). A report generated with Assessed/Unassessed has
  // only those calls in its query, so the preview can narrow within them, never widen.
  const changePreviewRankBy = (newRankBy) => {
    if (!report) return;
    setPreviewRankBy(newRankBy);
    if (gridPollRef.current) {
      clearTimeout(gridPollRef.current);
      gridPollRef.current = null;
    }
    setGrid(null);
    setGridError(null);
    setGridStatus('running');
    gridPollCountRef.current = 0;
    pollGrid(report.queryExecutionId, report.filterSummary, newRankBy);
  };

  // ── XLSX export ──────────────────────────────────────────────────────────────
  // Polls the report record itself (DynamoDB, via getReportDetail) while waiting on a
  // first-time result — not S3 — since nothing here needs a download URL until
  // Download is actually clicked. Only used for that first wait: a retry re-polls
  // through pollXlsxViaS3 below instead, because right after a retry clears the S3
  // markers, xlsxStatus in DynamoDB is still whatever the *previous* attempt left
  // behind until the freshly-invoked worker overwrites it — reading it here would
  // misreport a fresh attempt as still failed.
  const pollXlsx = useCallback((forReportId) => {
    xlsxPollRef.current = setTimeout(async () => {
      xlsxPollCountRef.current += 1;
      try {
        const data = await getReportDetail(forReportId);
        if (data.xlsxStatus === 'complete') {
          setXlsxStatus('succeeded');
          setAlert && setAlert({ variant: 'success', heading: 'XLSX Export Ready', text: 'Click Download XLSX to save the file.' });
          return;
        }
        if (data.xlsxStatus === 'failed') {
          setXlsxStatus('failed');
          setXlsxError(data.xlsxErrorMessage || 'XLSX generation failed.');
          return;
        }
        if (xlsxPollCountRef.current >= MAX_POLLS) {
          setXlsxStatus('failed');
          setXlsxError('XLSX generation is taking longer than expected. Please try again.');
          return;
        }
        pollXlsx(forReportId);
      } catch (err) {
        console.error('XLSX status check failed:', err);
        setXlsxStatus('failed');
        setXlsxError('Failed to check XLSX status. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, [setAlert]);

  // Real-time, S3-backed poll — used only right after a Retry, where DynamoDB's
  // xlsxStatus can't be trusted yet (see pollXlsx's comment above).
  const pollXlsxViaS3 = useCallback((queryExecutionId, filterSummary, rankBy, reportIdForRelay) => {
    xlsxPollRef.current = setTimeout(async () => {
      xlsxPollCountRef.current += 1;
      try {
        const res = await getQaReportStatus(queryExecutionId, filterSummary, rankBy, { mode: 'xlsx', reportId: reportIdForRelay });
        if (res.status === 'RUNNING') {
          if (xlsxPollCountRef.current >= MAX_POLLS) {
            setXlsxStatus('failed');
            setXlsxError('XLSX generation is taking longer than expected. Please try again.');
            return;
          }
          pollXlsxViaS3(queryExecutionId, filterSummary, rankBy, reportIdForRelay);
          return;
        }
        if (res.status === 'FAILED') {
          setXlsxStatus('failed');
          setXlsxError(res.message || 'XLSX generation failed.');
          return;
        }
        setXlsxStatus('succeeded');
        setAlert && setAlert({ variant: 'success', heading: 'XLSX Export Ready', text: 'Click Download XLSX to save the file.' });
      } catch (err) {
        console.error('XLSX status check failed:', err);
        setXlsxStatus('failed');
        setXlsxError('Failed to check XLSX status. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, [setAlert]);

  // ── CSV export ───────────────────────────────────────────────────────────────
  const pollCsv = useCallback((forReportId) => {
    csvPollRef.current = setTimeout(async () => {
      csvPollCountRef.current += 1;
      try {
        const data = await getReportDetail(forReportId);
        if (data.csvStatus === 'complete') {
          setCsvStatus('succeeded');
          setAlert && setAlert({ variant: 'success', heading: 'CSV Export Ready', text: 'Click Download CSV to save the file.' });
          return;
        }
        if (data.csvStatus === 'failed') {
          setCsvStatus('failed');
          setCsvError(data.csvErrorMessage || 'CSV generation failed.');
          return;
        }
        if (csvPollCountRef.current >= MAX_POLLS) {
          setCsvStatus('failed');
          setCsvError('CSV generation is taking longer than expected. Please try again.');
          return;
        }
        pollCsv(forReportId);
      } catch (err) {
        console.error('CSV status check failed:', err);
        setCsvStatus('failed');
        setCsvError('Failed to check CSV status. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, [setAlert]);

  const pollCsvViaS3 = useCallback((queryExecutionId, filterSummary, rankBy, reportIdForRelay) => {
    csvPollRef.current = setTimeout(async () => {
      csvPollCountRef.current += 1;
      try {
        const res = await getQaReportStatus(queryExecutionId, filterSummary, rankBy, { mode: 'csv', reportId: reportIdForRelay });
        if (res.status === 'RUNNING') {
          if (csvPollCountRef.current >= MAX_POLLS) {
            setCsvStatus('failed');
            setCsvError('CSV generation is taking longer than expected. Please try again.');
            return;
          }
          pollCsvViaS3(queryExecutionId, filterSummary, rankBy, reportIdForRelay);
          return;
        }
        if (res.status === 'FAILED') {
          setCsvStatus('failed');
          setCsvError(res.message || 'CSV generation failed.');
          return;
        }
        setCsvStatus('succeeded');
        setAlert && setAlert({ variant: 'success', heading: 'CSV Export Ready', text: 'Click Download CSV to save the file.' });
      } catch (err) {
        console.error('CSV status check failed:', err);
        setCsvStatus('failed');
        setCsvError('Failed to check CSV status. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, [setAlert]);

  // ── PDF export ───────────────────────────────────────────────────────────────
  // Moved above loadReport (unlike the render-order-matching XLSX/CSV pollers) since
  // loadReport now needs to resume a still-processing PDF job too — see its own
  // pdfStatus handling below.
  //
  // Polls the report record itself (DynamoDB, via getReportDetail) while waiting on
  // a result — not S3 — same reasoning as pollXlsx/pollCsv: nothing here needs a
  // download URL until Download is actually clicked, so there's no reason to pay for
  // an S3 read on every 2s poll for up to 3 minutes. The one exception is the instant
  // pdfStatus flips to complete: this still does a single S3-backed
  // getExecutiveSummaryStatus call right then, same as loadReport's own
  // already-complete branch below, so both download buttons (summary + question
  // analysis) are ready to click immediately rather than only after a first click
  // populates them.
  const pollExecSummary = useCallback((forReportId) => {
    execSummaryPollRef.current = setTimeout(async () => {
      execSummaryPollCountRef.current += 1;
      try {
        const data = await getReportDetail(forReportId);
        if (data.pdfStatus === 'complete' && data.pdfJobId) {
          try {
            const res = await getExecutiveSummaryStatus(data.pdfJobId);
            if (res.status === 'SUCCEEDED') {
              // The companion question-analysis document is best-effort: it can
              // succeed, fail on its own while the summary still succeeded, or be
              // skipped entirely when the report has no campaign filter.
              setQuestionAnalysisUrl(res.questionAnalysisUrl || null);
              setQuestionAnalysisError(res.questionAnalysisError || null);
              setExecSummaryStatus('succeeded');
              setAlert && setAlert({ variant: 'success', heading: 'PDF Ready', text: 'Click Download Executive Summary (PDF) to save the file.' });
              return;
            }
            // Registry says complete but S3 disagrees — this poll only reaches here
            // once pdfStatus has already flipped to complete, so this can only mean
            // the file has since expired past the bucket's retention window (or was
            // otherwise removed), never "still generating" — that already happened.
            // Stop polling for something that will never arrive.
            setExecSummaryStatus('expired');
            return;
          } catch (err) {
            console.error('Failed to load executive summary status:', err);
          }
        } else if (data.pdfStatus === 'failed') {
          setExecSummaryStatus('failed');
          setExecSummaryError(data.pdfErrorMessage || 'Executive summary generation failed.');
          return;
        }
        if (execSummaryPollCountRef.current >= MAX_POLLS) {
          setExecSummaryStatus('failed');
          setExecSummaryError('Executive summary generation is taking longer than expected. Please try again.');
          return;
        }
        pollExecSummary(forReportId);
      } catch (err) {
        console.error('Executive summary status check failed:', err);
        setExecSummaryStatus('failed');
        setExecSummaryError('Failed to check executive summary status. Please try again.');
      }
    }, POLL_INTERVAL_MS);
  }, [setAlert]);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await getReportDetail(reportId);
      setReport(data);
      setPreviewRankBy(data.rankBy || '');
      setHasLoaded(true);

      // XLSX/CSV: read straight off the report record we just fetched — no need to
      // wait out a poll interval for something we may already know the answer to.
      if (data.queryExecutionId) {
        if (data.xlsxStatus === 'complete') {
          setXlsxStatus('succeeded');
        } else if (data.xlsxStatus === 'failed') {
          setXlsxStatus('failed');
          setXlsxError(data.xlsxErrorMessage || 'XLSX generation failed.');
        } else {
          setXlsxStatus('running');
          xlsxPollCountRef.current = 0;
          pollXlsx(data.reportId);
        }

        if (data.csvStatus === 'complete') {
          setCsvStatus('succeeded');
        } else if (data.csvStatus === 'failed') {
          setCsvStatus('failed');
          setCsvError(data.csvErrorMessage || 'CSV generation failed.');
        } else {
          setCsvStatus('running');
          csvPollCountRef.current = 0;
          pollCsv(data.reportId);
        }
      }

      // PDF: pdfStatus persists once complete — a download no longer clears it (see
      // downloadExecutiveSummary) — so navigating away and back keeps showing Download
      // rather than losing track of an already-generated PDF and offering to
      // regenerate it by accident. Absent pdfStatus (never generated) leaves this at
      // its 'idle' default.
      if (data.pdfStatus === 'processing' && data.pdfJobId) {
        setExecSummaryJobId(data.pdfJobId);
        setExecSummaryStatus('running');
        execSummaryPollCountRef.current = 0;
        pollExecSummary(data.reportId);
      } else if (data.pdfStatus === 'complete' && data.pdfJobId) {
        setExecSummaryJobId(data.pdfJobId);
        setExecSummaryStatus('running');
        try {
          const res = await getExecutiveSummaryStatus(data.pdfJobId);
          if (res.status === 'SUCCEEDED') {
            setQuestionAnalysisUrl(res.questionAnalysisUrl || null);
            setQuestionAnalysisError(res.questionAnalysisError || null);
            setExecSummaryStatus('succeeded');
          } else if (res.status === 'FAILED') {
            setExecSummaryStatus('failed');
            setExecSummaryError(res.message || 'Executive summary generation failed.');
          } else {
            // The registry says complete, so anything other than SUCCEEDED here can
            // only mean the file has since expired past the bucket's retention
            // window (or was otherwise removed) — never "still generating", since
            // that already happened. Show the notice rather than polling for
            // something that will never arrive.
            setExecSummaryStatus('expired');
          }
        } catch (err) {
          console.error('Failed to load executive summary status:', err);
          setExecSummaryStatus('failed');
          setExecSummaryError('Failed to load the executive summary. Please try again.');
        }
      } else if (data.pdfStatus === 'failed' && data.pdfJobId) {
        setExecSummaryJobId(data.pdfJobId);
        setExecSummaryStatus('failed');
        setExecSummaryError(data.pdfErrorMessage || 'Executive summary generation failed.');
      }

      if (data.status === 'processing') {
        setGridStatus('running');
        gridPollCountRef.current = 0;
        pollGrid(data.queryExecutionId, data.filterSummary, data.rankBy);
      } else if (data.status === 'failed') {
        setGridStatus('failed');
        setGridError(data.errorMessage || 'Report generation failed.');
      } else if (data.status === 'complete') {
        // The registry only tracks status/recordCount — the grid itself still comes
        // from the same endpoint polling uses, just fetched once here instead of
        // repeatedly.
        setGridStatus('running');
        try {
          const res = await getQaReportStatus(data.queryExecutionId, data.filterSummary, data.rankBy);
          if (res.status === 'SUCCEEDED') {
            setGrid(res);
            setGridStatus('succeeded');
          } else if (res.status === 'FAILED') {
            setGridStatus('failed');
            setGridError(res.message || 'Report generation failed.');
          } else {
            // Shouldn't normally happen once the registry says complete, but poll
            // rather than get stuck if it does.
            gridPollCountRef.current = 0;
            pollGrid(data.queryExecutionId, data.filterSummary, data.rankBy);
          }
        } catch (err) {
          console.error('Failed to load report grid:', err);
          setGridStatus('failed');
          setGridError('Failed to load the report. Please try again.');
        }
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  }, [reportId, pollGrid, pollXlsx, pollCsv, pollExecSummary]);

  useEffect(() => {
    if (!hasLoaded && reportId) loadReport();
  }, [hasLoaded, reportId, loadReport]);

  useEffect(() => () => {
    if (gridPollRef.current) clearTimeout(gridPollRef.current);
    if (xlsxPollRef.current) clearTimeout(xlsxPollRef.current);
    if (csvPollRef.current) clearTimeout(csvPollRef.current);
    if (execSummaryPollRef.current) clearTimeout(execSummaryPollRef.current);
  }, []);

  const regenerateReportClick = async () => {
    if (!report) return;
    if (!window.confirm('Regenerate this report? This re-queries the latest call data and rebuilds the preview, XLSX, and CSV. A previously generated PDF is cleared and will need to be generated again.')) {
      return;
    }

    // Every poller in flight is chasing an artifact keyed to the queryExecutionId
    // that's about to be replaced — stop all of them before starting new ones.
    [gridPollRef, xlsxPollRef, csvPollRef, execSummaryPollRef].forEach((ref) => {
      if (ref.current) {
        clearTimeout(ref.current);
        ref.current = null;
      }
    });

    setRegenerating(true);
    try {
      const res = await regenerateReport(report.reportId);

      setReport((prev) => ({
        ...prev,
        queryExecutionId: res.queryExecutionId,
        filterSummary: res.filterSummary,
        status: 'processing',
        recordCount: undefined,
        errorMessage: undefined,
        xlsxStatus: undefined,
        xlsxErrorMessage: undefined,
        csvStatus: undefined,
        csvErrorMessage: undefined,
        pdfStatus: undefined,
        pdfJobId: undefined,
        pdfErrorMessage: undefined,
      }));

      setGrid(null);
      setGridError(null);
      setGridStatus('running');
      gridPollCountRef.current = 0;
      pollGrid(res.queryExecutionId, res.filterSummary, previewRankBy);

      setXlsxStatus('running');
      setXlsxError(null);
      xlsxPollCountRef.current = 0;
      pollXlsx(report.reportId);

      setCsvStatus('running');
      setCsvError(null);
      csvPollCountRef.current = 0;
      pollCsv(report.reportId);

      setExecSummaryStatus('idle');
      setExecSummaryError(null);
      setExecSummaryJobId(null);
      setQuestionAnalysisUrl(null);
      setQuestionAnalysisError(null);

      setAlert && setAlert({ variant: 'success', heading: 'Regenerating Report', text: 'Re-running the query and rebuilding the preview, XLSX, and CSV.' });
    } catch (err) {
      console.error('Failed to regenerate report:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to regenerate the report. Please try again.' });
    } finally {
      setRegenerating(false);
    }
  };

  const retryXlsxExport = async () => {
    if (!report) return;
    if (xlsxPollRef.current) {
      clearTimeout(xlsxPollRef.current);
      xlsxPollRef.current = null;
    }
    setXlsxStatus('running');
    setXlsxError(null);
    xlsxPollCountRef.current = 0;
    try {
      const res = await getQaReportStatus(report.queryExecutionId, report.filterSummary, report.rankBy, { mode: 'xlsx', retry: true, reportId: report.reportId });
      if (res.status === 'FAILED') {
        setXlsxStatus('failed');
        setXlsxError(res.message || 'XLSX generation failed.');
        return;
      }
      if (res.status === 'SUCCEEDED') {
        setXlsxStatus('succeeded');
        setAlert && setAlert({ variant: 'success', heading: 'XLSX Export Ready', text: 'Click Download XLSX to save the file.' });
        return;
      }
      pollXlsxViaS3(report.queryExecutionId, report.filterSummary, report.rankBy, report.reportId);
    } catch (err) {
      console.error('Failed to retry XLSX export:', err);
      setXlsxStatus('failed');
      setXlsxError('Failed to retry XLSX generation. Please try again.');
    }
  };

  // S3 presigned URLs expire (1hr, server-side) — re-check status right before
  // downloading rather than trusting a URL that may have been sitting in state since
  // the job finished. This, and Retry above, are the only two places this page ever
  // touches S3 — everything else reads the report record.
  const downloadXlsxExport = async () => {
    if (!report) return;
    try {
      const res = await getQaReportStatus(report.queryExecutionId, null, report.rankBy, { mode: 'xlsx' });
      if (!res.downloadUrl) {
        setAlert && setAlert({ variant: 'warning', heading: 'Not Ready Yet', text: 'The XLSX file is not ready yet. Please wait a moment and try again.' });
        return;
      }
      const link = document.createElement('a');
      link.href = res.downloadUrl;
      link.download = `qa-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setAlert && setAlert({ variant: 'success', heading: 'Download Started', text: 'qa-report.xlsx is downloading.' });
    } catch (err) {
      console.error('Failed to refresh XLSX download link:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to prepare the download link. Please try again.' });
    }
  };

  const retryCsvExport = async () => {
    if (!report) return;
    if (csvPollRef.current) {
      clearTimeout(csvPollRef.current);
      csvPollRef.current = null;
    }
    setCsvStatus('running');
    setCsvError(null);
    csvPollCountRef.current = 0;
    try {
      const res = await getQaReportStatus(report.queryExecutionId, report.filterSummary, report.rankBy, { mode: 'csv', retry: true, reportId: report.reportId });
      if (res.status === 'FAILED') {
        setCsvStatus('failed');
        setCsvError(res.message || 'CSV generation failed.');
        return;
      }
      if (res.status === 'SUCCEEDED') {
        setCsvStatus('succeeded');
        setAlert && setAlert({ variant: 'success', heading: 'CSV Export Ready', text: 'Click Download CSV to save the file.' });
        return;
      }
      pollCsvViaS3(report.queryExecutionId, report.filterSummary, report.rankBy, report.reportId);
    } catch (err) {
      console.error('Failed to retry CSV export:', err);
      setCsvStatus('failed');
      setCsvError('Failed to retry CSV generation. Please try again.');
    }
  };

  const downloadCsvExport = async () => {
    if (!report) return;
    try {
      const res = await getQaReportStatus(report.queryExecutionId, null, report.rankBy, { mode: 'csv' });
      if (!res.downloadUrl) {
        setAlert && setAlert({ variant: 'warning', heading: 'Not Ready Yet', text: 'The CSV file is not ready yet. Please wait a moment and try again.' });
        return;
      }
      const link = document.createElement('a');
      link.href = res.downloadUrl;
      link.download = `qa-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setAlert && setAlert({ variant: 'success', heading: 'Download Started', text: 'qa-report.csv is downloading.' });
    } catch (err) {
      console.error('Failed to refresh CSV download link:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to prepare the download link. Please try again.' });
    }
  };

  const generateExecutiveSummary = async () => {
    if (!report) return;
    if (execSummaryPollRef.current) {
      clearTimeout(execSummaryPollRef.current);
      execSummaryPollRef.current = null;
    }
    setExecSummaryStatus('starting');
    setExecSummaryError(null);
    setQuestionAnalysisUrl(null);
    setQuestionAnalysisError(null);
    execSummaryPollCountRef.current = 0;

    try {
      const params = {
        dateFrom: report.dateFrom,
        dateTo: report.dateTo,
        campaign: report.campaign,
        scorecard: report.scorecard,
        agent: report.agent,
        guid: report.guid,
        // Sales Status, Focus and Dates by complete the report's scope. The question-analysis
        // document always runs its own queries from these params, so without them it would
        // ignore all three. rankBy is the Focus the report was generated with, not the
        // preview's; the backend ignores a top/bottom rank, which no query can apply.
        salesFilter: report.salesFilter,
        rankBy: report.rankBy,
        dateBasis: report.dateBasis,
        reportId: report.reportId,
      };
      const res = await startExecutiveSummary(params);
      if (!res.jobId) {
        throw new Error('No job id returned');
      }
      setExecSummaryJobId(res.jobId);
      setExecSummaryStatus('running');
      pollExecSummary(report.reportId);
    } catch (err) {
      console.error('Failed to start executive summary generation:', err);
      setExecSummaryStatus('failed');
      setExecSummaryError('Failed to start executive summary generation. Please try again.');
    }
  };

  // pdfStatus stays 'complete' after this — unlike the old behaviour, downloading no
  // longer clears the report's PDF tracking. That's deliberate: it's what makes the
  // PDF survive a navigate-away-and-back showing Download instead of forgetting a
  // PDF was ever generated. S3 presigned URLs still expire (1hr), so this still
  // re-checks status right before downloading rather than trusting a URL that may
  // have been sitting in state since the job finished — same as XLSX/CSV.
  const downloadExecutiveSummary = async () => {
    if (!execSummaryJobId || !report) return;
    try {
      const res = await getExecutiveSummaryStatus(execSummaryJobId);
      if (!res.downloadUrl) {
        // This is only ever called once execSummaryStatus is already 'succeeded' —
        // we already believed this PDF existed, so a missing downloadUrl now means
        // the file has expired/been removed since, not that it's still generating.
        setExecSummaryStatus('expired');
        return;
      }
      const link = document.createElement('a');
      link.href = res.downloadUrl;
      link.download = 'qa-executive-summary.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setAlert && setAlert({ variant: 'success', heading: 'Download Started', text: 'qa-executive-summary.pdf is downloading.' });
    } catch (err) {
      console.error('Failed to refresh executive summary download link:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to prepare the download link. Please try again.' });
    }
  };

  const downloadQuestionAnalysis = async () => {
    if (!execSummaryJobId) return;
    try {
      const res = await getExecutiveSummaryStatus(execSummaryJobId);
      if (!res.questionAnalysisUrl) {
        setAlert && setAlert({ variant: 'warning', heading: 'Not Ready Yet', text: 'The question analysis document is not ready yet. Please wait a moment and try again.' });
        return;
      }
      const link = document.createElement('a');
      link.href = res.questionAnalysisUrl;
      link.download = 'qa-question-analysis.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setAlert && setAlert({ variant: 'success', heading: 'Download Started', text: 'qa-question-analysis.pdf is downloading.' });
    } catch (err) {
      console.error('Failed to refresh question analysis download link:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to prepare the download link. Please try again.' });
    }
  };

  // "At a Glance" charts (meeting feedback: the report needed a visual, not
  // just the grid). Computed from the previewed calls, since that's all the
  // grid endpoint returns — the caption says so.
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState(null);
  const chatEndRef = useRef(null);
  const chatLoadedFor = useRef(null);

  // The conversation survives reloads: persisted per report in this browser,
  // until the user clears it. Managed by effects rather than a storage hook so
  // the key can follow reportId without the component remounting.
  useEffect(() => {
    chatLoadedFor.current = null;
    let stored = [];
    try {
      const raw = window.localStorage.getItem(`reportChat:${reportId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          stored = parsed.filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string');
        }
      }
    } catch (err) { /* stored chat is a convenience — never let it break the page */ }
    setChatMessages(stored);
    setChatError(null);
    chatLoadedFor.current = reportId;
  }, [reportId]);

  useEffect(() => {
    // Skip until the stored history for THIS report has loaded, or the initial
    // empty state would wipe what we're about to restore.
    if (chatLoadedFor.current !== reportId) return;
    try {
      if (chatMessages.length === 0) window.localStorage.removeItem(`reportChat:${reportId}`);
      else window.localStorage.setItem(`reportChat:${reportId}`, JSON.stringify(chatMessages.slice(-50)));
    } catch (err) { /* quota/private mode — the chat still works for this session */ }
  }, [chatMessages, reportId]);

  const clearChat = () => {
    setChatMessages([]);
    setChatError(null);
  };

  // Only after the user sends something — auto-scrolling to a restored
  // conversation would yank the page down to the chat card on every reload.
  const chatInteracted = useRef(false);
  useEffect(() => {
    if (chatInteracted.current && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [chatMessages, chatBusy]);

  const gridStats = useMemo(() => {
    if (!grid || !grid.calls || grid.calls.length === 0) return null;
    const outcome = { pass: 0, medium: 0, fail: 0, unscored: 0 };
    grid.calls.forEach((call) => {
      const score = Number(call.qaScore);
      if (call.qaScore === '' || call.qaScore == null || Number.isNaN(score)) outcome.unscored += 1;
      else if (score >= 90) outcome.pass += 1;
      else if (score < 60) outcome.fail += 1;
      else outcome.medium += 1;
    });
    const perQuestion = (grid.columns || [])
      .map((q) => {
        let pass = 0;
        let total = 0;
        grid.calls.forEach((call) => {
          const statusRow = (call.rows || []).find((r) => r.label === 'Status');
          const status = String(statusRow?.values?.[q] || '').toLowerCase();
          // N/A was never scored, so it counts toward neither side of the rate.
          if (!status || status === 'n/a') return;
          total += 1;
          if (status === 'success' || status === 'pass') pass += 1;
        });
        return { question: q, rate: total ? Math.round((pass / total) * 100) : null };
      })
      .filter((e) => e.rate != null);
    return { outcome, perQuestion };
  }, [grid]);

  const outcomeChartData = useMemo(() => {
    if (!gridStats) return null;
    const { outcome } = gridStats;
    // Same thresholds and colours the grid's row banding uses.
    const entries = [
      ['Pass (≥90)', outcome.pass, '#14b8a6'],
      ['Medium', outcome.medium, '#f59e0b'],
      ['Fail (<60)', outcome.fail, '#ef4444'],
      ['Unscored', outcome.unscored, '#9ca3af'],
    ].filter(([, count]) => count > 0);
    return {
      labels: entries.map(([label]) => label),
      datasets: [{ data: entries.map(([, count]) => count), backgroundColor: entries.map(([, , color]) => color) }],
    };
  }, [gridStats]);

  const questionChartData = useMemo(() => {
    if (!gridStats) return null;
    return {
      labels: gridStats.perQuestion.map((e) => (e.question.length > 40 ? `${e.question.slice(0, 37)}…` : e.question)),
      datasets: [{
        label: 'Pass rate',
        data: gridStats.perQuestion.map((e) => e.rate),
        backgroundColor: '#14b8a6',
        borderRadius: 4,
        maxBarThickness: 20,
      }],
    };
  }, [gridStats]);

  // ── Report chat ("talk to the report", meeting ask 2026-08-24) ──────────────
  // Questions answered server-side from this report's own data; the history is
  // held here and replayed with each request — the backend is stateless.
  const sendChat = async () => {
    const question = chatInput.trim();
    if (!question || chatBusy) return;
    chatInteracted.current = true;
    setChatError(null);
    setChatInput('');
    const history = chatMessages.slice(-10);
    setChatMessages((prev) => [...prev, { role: 'user', content: question }]);
    setChatBusy(true);
    try {
      const res = await postReportChat(reportId, question, history);
      const answer = res.status === 'SUCCEEDED' ? res.answer : (res.message || 'No answer returned.');
      setChatMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
    } catch (err) {
      console.error('Report chat failed:', err);
      setChatError('Failed to get an answer. Please try again.');
    } finally {
      setChatBusy(false);
    }
  };

  if (isLoading && !hasLoaded) {
    return <LoadingState label="Loading report" />;
  }

  if (loadError || !report) {
    return (
      <div className="card border-red-200 bg-red-50">
        <p className="text-sm text-red-700">Failed to load report. Please try again.</p>
        <button className="btn-outline mt-3" onClick={() => navigate('/reports')}>
          Back to Reports
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-y-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Report Details</h1>
          <p className="text-gray-500 text-sm mt-1">{report.filterSummary || `${report.dateFrom} to ${report.dateTo}`}</p>
        </div>
        <button
          className="btn-outline"
          onClick={regenerateReportClick}
          disabled={regenerating || gridStatus === 'running'}
          title="Re-run this report against the latest call data — use this after a call has been manually re-scored."
        >
          <FaSyncAlt className={regenerating ? 'animate-spin' : ''} />
          {regenerating ? 'Regenerating...' : 'Regenerate Report'}
        </button>
      </div>

      {/* Landing here right after Generate Report, the page otherwise looks
          finished while the grid quietly builds at the bottom — say so up top. */}
      {gridStatus === 'running' && (
        <div className="card border-cloudza-200 bg-cloudza-50/60">
          <div className="flex items-center gap-3">
            <InlineSpinner />
            <div>
              <p className="text-sm font-semibold text-gray-800">Generating report…</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Running the query and building the results — this usually takes under a minute.
                The preview below fills in automatically when it's ready.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Filters</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-5">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Date Range</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{report.dateFrom} to {report.dateTo}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Dates by</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{DATE_BASIS_LABELS[report.dateBasis] || 'Call date'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Campaign</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{report.campaign || 'All'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Scorecard</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{report.scorecard || 'All'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Agent</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{report.agent || 'All'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">GUID</p>
            <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{report.guid || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Focus (as generated)</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{RANK_LABELS[report.rankBy] || 'All calls'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Sales Status</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{SALES_LABELS[report.salesFilter] || 'All calls'}</p>
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Exports</p>
          <div className="flex-1" />
          <button
            ref={downloadBtnRef}
            className="btn-primary"
            onClick={() => {
              if (!downloadMenuOpen) updateDownloadMenuPos();
              setDownloadMenuOpen((v) => !v);
            }}
            aria-haspopup="true"
            aria-expanded={downloadMenuOpen}
          >
            <FaDownload /> Download
            <FaChevronDown
              className={`h-2.5 w-2.5 transition-transform duration-200 ${downloadMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
        {downloadMenuOpen && createPortal(
          <div
            ref={downloadMenuRef}
            className="popup-surface fixed z-[110] w-80 rounded-lg border bg-white py-1 shadow-lg"
            style={{ top: downloadMenuPos.top, left: downloadMenuPos.left }}
            role="menu"
          >
            {xlsxStatus === 'succeeded' ? (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => { setDownloadMenuOpen(false); downloadXlsxExport(); }}
              >
                <FaFileExcel className="text-green-600" /> Download XLSX
              </button>
            ) : xlsxStatus === 'failed' ? (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => { setDownloadMenuOpen(false); retryXlsxExport(); }}
              >
                <FaSyncAlt className="text-gray-500" /> Retry XLSX
              </button>
            ) : (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-400 cursor-not-allowed"
                disabled
              >
                <InlineSpinner /> Generating XLSX...
              </button>
            )}

            {csvStatus === 'succeeded' ? (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => { setDownloadMenuOpen(false); downloadCsvExport(); }}
              >
                <FaFileCsv className="text-green-600" /> Download CSV
              </button>
            ) : csvStatus === 'failed' ? (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => { setDownloadMenuOpen(false); retryCsvExport(); }}
              >
                <FaSyncAlt className="text-gray-500" /> Retry CSV
              </button>
            ) : (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-400 cursor-not-allowed"
                disabled
              >
                <InlineSpinner /> Generating CSV...
              </button>
            )}

            <div className="my-1 border-t border-gray-100" />

            {execSummaryStatus === 'starting' || execSummaryStatus === 'running' ? (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-400 cursor-not-allowed"
                disabled
              >
                <InlineSpinner /> Generating Executive Summary...
              </button>
            ) : (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => {
                  setDownloadMenuOpen(false);
                  if (execSummaryStatus === 'succeeded') downloadExecutiveSummary();
                  else generateExecutiveSummary();
                }}
              >
                <FaFilePdf className="text-red-500" />
                {execSummaryStatus === 'succeeded'
                  ? 'Download Executive Summary (PDF)'
                  : 'Generate Executive Summary (PDF)'}
              </button>
            )}
            {execSummaryStatus === 'succeeded' && (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => { setDownloadMenuOpen(false); generateExecutiveSummary(); }}
              >
                <FaSyncAlt className="text-gray-500" /> Regenerate PDF
              </button>
            )}
            {execSummaryStatus === 'succeeded' && questionAnalysisUrl && (
              <button
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                onClick={() => { setDownloadMenuOpen(false); downloadQuestionAnalysis(); }}
              >
                <FaFilePdf className="text-red-500" /> Download Question Analysis (PDF)
              </button>
            )}
          </div>,
          document.body
        )}

        {xlsxStatus === 'failed' && xlsxError && (
          <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">{xlsxError}</div>
        )}
        {csvStatus === 'failed' && csvError && (
          <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">{csvError}</div>
        )}
        {execSummaryStatus === 'failed' && execSummaryError && (
          <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">{execSummaryError}</div>
        )}
        {execSummaryStatus === 'succeeded' && questionAnalysisError && (
          <div className="p-4 rounded-lg border bg-yellow-50 border-yellow-200 text-yellow-800">
            The executive summary was generated, but the question analysis document could not be
            produced: {questionAnalysisError}
          </div>
        )}
        {/* Absent rather than broken: the companion document is per-campaign, so an unfiltered
            report has nothing to break down. */}
        {execSummaryStatus === 'succeeded' && !questionAnalysisUrl && !questionAnalysisError && (
          <div className="p-4 rounded-lg border bg-gray-50 border-gray-200 text-gray-600 text-sm">
            The question analysis document breaks results down per campaign, so it is only produced
            when the report is filtered to at least one campaign.
          </div>
        )}
        {execSummaryStatus === 'expired' && (
          <div className="p-4 rounded-lg border bg-yellow-50 border-yellow-200 text-yellow-800">
            The previously generated PDF is no longer available — it's past this account's data
            retention period. Click Generate PDF to create a new one.
          </div>
        )}
      </div>

      {gridStatus === 'succeeded' && gridStats && (
        <div className="card">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">At a Glance</p>
          <p className="text-sm text-gray-500 mb-4">
            Computed from the {grid.calls.length} previewed call{grid.calls.length === 1 ? '' : 's'}
            {grid.calls.length < grid.recordCount ? ` (of ${grid.recordCount} found)` : ''}.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Call outcomes by QA score</h3>
              <div className="h-56">
                <Doughnut
                  data={outcomeChartData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom' } },
                  }}
                />
              </div>
            </div>
            {gridStats.perQuestion.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Pass rate by question</h3>
                <div style={{ height: Math.max(160, gridStats.perQuestion.length * 28 + 48) }}>
                  <Bar
                    data={questionChartData}
                    options={{
                      indexAxis: 'y',
                      maintainAspectRatio: false,
                      scales: { x: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } } },
                      plugins: {
                        legend: { display: false },
                        tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.x}% pass` } },
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <FaComments className="text-cloudza-600" />
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Ask About This Report</p>
          </div>
          {chatMessages.length > 0 && (
            <button
              type="button"
              className="text-xs font-medium text-gray-400 hover:text-red-600 transition-colors"
              onClick={clearChat}
              title="Clear this report's saved conversation"
            >
              Clear conversation
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Answers are AI-generated from this report's data only — verify important figures against the exports above.
        </p>
        {chatMessages.length > 0 && (
          <div className="max-h-80 overflow-y-auto space-y-3 mb-4 pr-1">
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === 'user' ? 'bg-cloudza-600 text-white' : 'bg-gray-100 text-gray-800'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {chatBusy && (
              <div className="flex justify-start">
                <div className="rounded-xl px-3.5 py-2.5 bg-gray-100 text-sm text-gray-500 flex items-center gap-2">
                  <InlineSpinner /> Thinking…
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
        {chatError && <p className="text-sm text-red-600 mb-2">{chatError}</p>}
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); sendChat(); }}>
          <input
            className="input-field flex-1"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder={gridStatus === 'running' ? 'Available once the report has generated…' : 'e.g. Which agent had the lowest pass rate, and on which questions?'}
            disabled={chatBusy || gridStatus === 'running'}
            maxLength={2000}
            aria-label="Ask a question about this report"
          />
          <button type="submit" className="btn-primary flex-shrink-0" disabled={chatBusy || gridStatus === 'running' || !chatInput.trim()}>
            <FaPaperPlane /> Ask
          </button>
        </form>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Preview</p>
            {gridStatus === 'succeeded' && grid && (
              <p className="text-sm text-gray-500 mt-1">
                {grid.calls.length < grid.recordCount
                  ? `Showing the first ${grid.calls.length} of ${grid.recordCount} calls found — download XLSX or CSV above for the complete dataset.`
                  : `${grid.recordCount} call${grid.recordCount === 1 ? '' : 's'} found.`}
              </p>
            )}
          </div>
          <div className="w-64">
            <Select
              options={RANK_OPTIONS}
              value={RANK_OPTIONS.find((o) => o.value === previewRankBy) || null}
              onChange={(opt) => changePreviewRankBy(opt ? opt.value : '')}
              placeholder="All calls"
              isClearable
            />
          </div>
        </div>
        {previewRankBy !== (report.rankBy || '') && (
          <p className="text-xs text-gray-500 mb-3">
            Previewing a different Focus filter than this report was generated with — the
            downloads above still reflect {RANK_LABELS[report.rankBy] || 'All calls'}.
          </p>
        )}
        {gridStatus === 'running' && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <InlineSpinner /> Building report preview...
          </div>
        )}
        {gridStatus === 'failed' && gridError && (
          <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">{gridError}</div>
        )}
        {gridStatus === 'succeeded' && grid && (
          <SpreadsheetGrid columns={grid.columns} calls={grid.calls} />
        )}
      </div>
    </div>
  );
};

export default ReportDetail;
